import { Link, useParams } from 'react-router-dom';
import { Linkedin, Instagram } from 'lucide-react';

const LOGO_URL = "/images/hg-logo.png";

const Footer = () => {
  const { locale } = useParams();

  const services = [
    { label: 'Pharma Engineering & PMC', path: '/services/pharma-engineering' },
    { label: 'Sustainability & ESG', path: '/services/sustainability' },
    { label: 'Finance & Loans', path: '/services/finance-loans' },
    { label: 'Capital Markets', path: '/services/capital-markets' },
    { label: 'Mergers & Acquisitions', path: '/services/mergers-acquisitions' },
  ];

  const company = [
    { label: 'About', path: '/about' },
    { label: 'Insights', path: '/insights' },
    { label: 'Case Studies', path: '/case-studies' },
    { label: 'Careers', path: '/careers' },
    { label: 'Contact', path: '/contact' },
  ];

  const offices = [
    { city: 'Amsterdam', country: 'Netherlands', flag: '🇳🇱' },
    { city: 'Nashik', country: 'India', flag: '🇮🇳' },
    { city: 'Pune', country: 'India', flag: '🇮🇳' },
  ];

  return (
    <footer className="bg-light-grey border-t border-gray-200" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Column 1 - Logo & Description */}
          <div>
            <Link to={`/${locale}`} className="inline-block mb-6">
              <img 
                src={LOGO_URL} 
                alt="H&G - Horizon & Greenprint" 
                className="h-12 w-auto"
              />
            </Link>
            <p className="text-mid-grey text-sm leading-relaxed mb-6">
              A global partner across engineering, sustainability, finance, capital markets, and strategic advisory — for organizations building the next decade.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.linkedin.com/showcase/hng-global/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center text-teal hover:bg-teal hover:text-white transition-colors"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/hngcxo/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center text-teal hover:bg-teal hover:text-white transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Column 2 - Services */}
          <div>
            <h4 className="font-semibold text-navy text-lg mb-6">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.path}>
                  <Link 
                    to={`/${locale}${service.path}`}
                    className="text-mid-grey hover:text-teal transition-colors text-sm"
                    data-testid={`footer-${service.path.split('/').pop()}`}
                  >
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Company */}
          <div>
            <h4 className="font-semibold text-navy text-lg mb-6">Company</h4>
            <ul className="space-y-3">
              {company.map((item) => (
                <li key={item.path}>
                  <Link 
                    to={`/${locale}${item.path}`}
                    className="text-mid-grey hover:text-teal transition-colors text-sm"
                    data-testid={`footer-${item.path.split('/').pop()}`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 - Global Offices */}
          <div>
            <h4 className="font-semibold text-navy text-lg mb-6">Global Offices</h4>
            <ul className="space-y-3">
              {offices.map((office) => (
                <li key={office.city} className="text-mid-grey text-sm">
                  <span className="mr-2">{office.flag}</span>
                  {office.city}, {office.country}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-300 flex flex-col md:flex-row justify-between items-center">
          <p className="text-mid-grey text-sm">
            © 2025 H&G (Horizon & Greenprint). All Rights Reserved.
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to={`/${locale}/privacy-policy`} className="text-mid-grey hover:text-teal text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to={`/${locale}/terms-of-use`} className="text-mid-grey hover:text-teal text-sm transition-colors">
              Terms of Use
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
